import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { Z_LAYERS } from "../../windowManager/zLayers";
import { PdfIcons } from "./pdfIcons";
import "./pdfEditor.css";
import { alleFenster } from "../../windowManager/browserFenster/fensterRegister";
import { dateinameOhneSchraegstrich, hatSchraegstrich } from "../../../utils/fileUtils";

/** Ein Bild wird gespeichert: als Bild (bleibt ein Bild) oder als PDF (wird umgewandelt). */
export type BildZiel = "bild" | "pdf";

/** Aus einer früheren Fassung heraus: neueste Fassung des Dokuments oder eigenes Dokument daneben. */
export type FassungZiel = "neueste" | "eigenes";

/**
 * „Fassung speichern" — eigenes Popup des PDF-Editors ohne Fluent (User
 * 02.10.2026, Projektregel in `redesign/bestaetigen`). Dateiname und
 * Kommentar, dazu die Hinweise, was beim Speichern unwiderruflich passiert.
 * Enter im Namensfeld speichert, Esc schließt (außer während des Speicherns).
 *
 * Bei einem BILD (User 04.10.2026) steht darüber die Wahl „Als Bild speichern“
 * oder „Als PDF speichern“ — das Bild bleibt ein Bild, lässt sich am Ende aber
 * auch in ein PDF umwandeln.
 *
 * Portaliert nach `document.body` und liegt auf `Z_LAYERS.ueberFenster`: der
 * Editor läuft meist in einem Fenster des Fenster-Managers (Layer 9000), ein
 * kleinerer z-index verschwand hinter der Fensterkarte (User 02.10.2026).
 */
export const SpeichernPopup: React.FC<{
    offen: boolean;
    name: string;
    onName: (v: string) => void;
    kommentar: string;
    onKommentar: (v: string) => void;
    saving: boolean;
    onSpeichern: () => void;
    onSchliessen: () => void;
    anzahlEntfernen: number;
    anzahlVerschoben: number;
    anzahlZeilen: number;
    /** Geschwärzte Stellen (T-363). */
    anzahlGeschwaerzt?: number;
    /**
     * Nur beim Bearbeiten einer FRÜHEREN Fassung (User 07.10.2026: „fragen, ob
     * das die neueste Version sein soll oder anders abspeichern“).
     */
    fassung?: { versionNo: number; ziel: FassungZiel; onZiel: (z: FassungZiel) => void };
    /** Nur beim Bearbeiten eines Bildes: die Wahl des Zielformats. */
    bild?: {
        ziel: BildZiel;
        onZiel: (z: BildZiel) => void;
        /** So heißt das Bildformat für den Nutzer — „JPG“, „PNG“ … */
        formatName: string;
        /** Das Bildformat wechselt beim Speichern (GIF/BMP → PNG). */
        formatWechsel: boolean;
        /** Nichts geändert — als Bild gäbe es nichts zu speichern. */
        unveraendert: boolean;
    };
}> = ({ offen, name, onName, kommentar, onKommentar, saving, onSpeichern, onSchliessen, anzahlEntfernen, anzahlVerschoben, anzahlZeilen, anzahlGeschwaerzt = 0, bild, fassung }) => {
    const nameRef = React.useRef<HTMLInputElement | null>(null);
    // Fokus NUR beim Öffnen. Hing er mit am Esc-Handler (dessen Abhängigkeiten
    // `saving`/`onSchliessen` sich bei jedem Rendern ändern — der Editor reicht
    // `onSchliessen` inline herein), sprang er nach jedem Tastendruck im
    // Kommentar zurück ins Namensfeld und markierte den Namen: der nächste
    // Buchstabe überschrieb den Dateinamen (User 07.10.2026).
    React.useEffect(() => {
        if (!offen) return;
        nameRef.current?.focus();
        nameRef.current?.select();
    }, [offen]);
    const schliessenRef = React.useRef({ saving, onSchliessen });
    schliessenRef.current = { saving, onSchliessen };
    React.useEffect(() => {
        if (!offen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !schliessenRef.current.saving) schliessenRef.current.onSchliessen();
        };
        alleFenster.addEventListener("keydown", onKey);
        return () => alleFenster.removeEventListener("keydown", onKey);
    }, [offen]);
    if (!offen) return null;
    const nichtsZuTun = !!bild && bild.ziel === "bild" && bild.unveraendert;
    const kann = !saving && name.trim().length > 0 && !nichtsZuTun;
    const alsPdf = bild?.ziel === "pdf";
    const alsEigenes = fassung?.ziel === "eigenes";
    return createPortal(
        <div
            className="pdfx pdfx-scrim"
            style={{ zIndex: Z_LAYERS.ueberFenster }}
            onMouseDown={(e) => e.target === e.currentTarget && !saving && onSchliessen()}
        >
            <div className="pdfx-popup" role="dialog" aria-modal="true" aria-labelledby="pdfx-speichern-titel">
                <div className="pdfx-popup-kopf">
                    <div className="pdfx-popup-kicker">
                        {fassung ? `Fassung ${fassung.versionNo} bearbeitet` : bild ? "Bild bearbeiten" : "PDF-Editor"}
                    </div>
                    <h2 id="pdfx-speichern-titel">{alsEigenes ? "Als eigenes Dokument speichern" : "Fassung speichern"}</h2>
                </div>
                <div className="pdfx-popup-body">
                    {fassung && (
                        <div className="pdfx-wahl" role="radiogroup" aria-label="Speichern als">
                            <button
                                type="button"
                                role="radio"
                                aria-checked={!alsEigenes}
                                disabled={saving}
                                onClick={() => fassung.onZiel("neueste")}
                            >
                                {PdfIcons.fassungen}
                                <span>
                                    <strong>Als neueste Fassung</strong>
                                    <small>Ersetzt den aktuellen Stand des Dokuments. Der bisherige bleibt in der Historie.</small>
                                </span>
                            </button>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={alsEigenes}
                                disabled={saving}
                                onClick={() => fassung.onZiel("eigenes")}
                            >
                                {PdfIcons.pdf}
                                <span>
                                    <strong>Als eigenes Dokument</strong>
                                    <small>Legt eine neue Datei daneben ab. Das bisherige Dokument bleibt, wie es ist.</small>
                                </span>
                            </button>
                        </div>
                    )}
                    {bild && (
                        <div className="pdfx-wahl" role="radiogroup" aria-label="Speichern als">
                            <button
                                type="button"
                                role="radio"
                                aria-checked={!alsPdf}
                                disabled={saving}
                                onClick={() => bild.onZiel("bild")}
                            >
                                {PdfIcons.bild}
                                <span>
                                    <strong>Als Bild speichern</strong>
                                    <small>
                                        {bild.formatWechsel
                                            ? `Bleibt ein Bild — wird als ${bild.formatName} gespeichert.`
                                            : `Bleibt ein Bild (${bild.formatName}).`}
                                    </small>
                                </span>
                            </button>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={alsPdf}
                                disabled={saving}
                                onClick={() => bild.onZiel("pdf")}
                            >
                                {PdfIcons.pdf}
                                <span>
                                    <strong>Als PDF speichern</strong>
                                    <small>Aus dem Bild wird ein PDF mit einer Seite.</small>
                                </span>
                            </button>
                        </div>
                    )}
                    <label className="pdfx-feld">
                        Dateiname
                        <input
                            ref={nameRef}
                            value={name}
                            onChange={(e) => onName(e.target.value)}
                            disabled={saving}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && kann) {
                                    e.preventDefault();
                                    onSpeichern();
                                }
                            }}
                        />
                    </label>
                    {hatSchraegstrich(name) && (
                        <div className="pdfx-schraegstrich" role="status">
                            „/“ und „\“ sind in Dateinamen nicht möglich und werden durch „-“ ersetzt. Gespeichert wird:{" "}
                            <b>{dateinameOhneSchraegstrich(name.trim())}</b>
                        </div>
                    )}
                    {!alsEigenes && (
                        <label className="pdfx-feld">
                            Kommentar
                            <textarea
                                value={kommentar}
                                onChange={(e) => onKommentar(e.target.value)}
                                placeholder="Was hat sich geändert?"
                                disabled={saving}
                            />
                            <small>
                                Steht in der Historie bei dieser Fassung — z. B. „Unterschrift ergänzt“. Was geändert
                                wurde, hält KMVS selbst fest.
                            </small>
                        </label>
                    )}
                    {bild && !alsPdf && !nichtsZuTun && (
                        <p className="pdfx-note" role="note">
                            Texte, Zeichnungen, Unterschriften und Stempel werden fest ins Bild eingerechnet und lassen
                            sich danach nicht mehr einzeln ändern. Die vorige Fassung bleibt in der Historie.
                        </p>
                    )}
                    {nichtsZuTun && (
                        <p className="pdfx-note" role="note">
                            Das Bild ist unverändert — als Bild gibt es nichts zu speichern. Zum Umwandeln „Als PDF
                            speichern“ wählen.
                        </p>
                    )}
                    {bild && alsPdf && (
                        <p className="pdfx-note" role="note">
                            Das Dokument ist danach ein PDF, der Dateiname endet auf „.pdf“. Das Bild bleibt als vorige
                            Fassung in der Historie und lässt sich dort wiederherstellen.
                        </p>
                    )}
                    {anzahlEntfernen > 0 && !(bild && !alsPdf) && (
                        <p className="pdfx-note" role="note">
                            Entfernte Bereiche sind danach unwiderruflich aus dieser Fassung gelöscht. Die vorige Fassung
                            bleibt in der Historie. Formularfelder, eingefügte Texte, Unterschriften und Stempel, die eine
                            Markierung berühren, werden ganz entfernt.
                        </p>
                    )}
                    {anzahlGeschwaerzt > 0 && (
                        <p className="pdfx-note" role="note">
                            Geschwärzte Stellen werden echt aus dieser Fassung gelöscht — an ihrer Stelle steht ein
                            schwarzer Balken, darunter ist nichts mehr. Die vorige Fassung bleibt in der Historie.
                        </p>
                    )}
                    {anzahlVerschoben > 0 && !(bild && !alsPdf) && (
                        <p className="pdfx-note" role="note">
                            Verschobene Ausschnitte werden als Bild eingefügt — ihr Text ist danach nicht mehr durchsuchbar.
                            Die Quelle ist aus dieser Fassung entfernt.
                        </p>
                    )}
                    {anzahlZeilen > 0 && (
                        <p className="pdfx-note" role="note">
                            Geänderte Zeilen werden in einer PDF-Standardschrift (Helvetica, Times oder Courier) neu
                            geschrieben, der alte Text ist aus dieser Fassung entfernt.
                        </p>
                    )}
                </div>
                <div className="pdfx-popup-foot">
                    <button type="button" className="pdfx-btn" disabled={saving} onClick={onSchliessen}>
                        Abbrechen
                    </button>
                    <button type="button" className="pdfx-btn primary" disabled={!kann} onClick={onSpeichern}>
                        {saving ? "Wird gespeichert…" : "Speichern"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
